import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as marketApi from '../api/marketplace';
import { useOrg } from '../lib/org';
import { Spinner, ErrorAlert } from '../components/ui';
import VendorLogo from '../components/VendorLogo';
import JoinInterestList from '../components/JoinInterestList';
import VendorReviews from '../components/VendorReviews';
import { LISTING_STATUS_LABELS, priceLabel, rewardProgramLabel } from '../lib/marketplace';
import { cn, ui } from '../lib/ui';

/**
 * /marketplace/vendors/:vendorId: the vendor page (docs/MarketplaceDesign.md
 * section 3.3) as the active organization shows it. Contact details come
 * back only for members while the vendor is listed here.
 */
export default function VendorPage() {
  const { vendorId } = useParams();
  const { activeOrg } = useOrg();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    marketApi
      .getVendor(vendorId)
      .then(d => live && setData(d))
      .catch(err => live && setError(err));
    return () => {
      live = false;
    };
  }, [vendorId, activeOrg?.orgId]);

  const back = (
    <p className="mb-4">
      <Link to="/marketplace">Back to the marketplace</Link>
    </p>
  );
  if (error) {
    return (
      <>
        {back}
        <ErrorAlert error={error} />
      </>
    );
  }
  if (!data) return <Spinner label="Loading the vendor..." />;

  const { vendor, listing, isOwner } = data;
  const contact = vendor.contact || {};
  const hasContact = Boolean(contact.email || contact.phone || contact.website);

  return (
    <>
      {back}
      <header className={cn(ui.card, 'mb-6 flex flex-wrap items-center gap-4')}>
        <VendorLogo vendor={vendor} size="size-20" />
        <div className="min-w-0 flex-1">
          <h1 className={cn(ui.h1, 'mb-1')}>{vendor.businessName}</h1>
          <p className={cn(ui.muted, 'm-0')}>
            {(listing?.categories || []).map(c => c.name).join(', ')}
            {vendor.serviceArea || contact.serviceArea
              ? ` - serves ${vendor.serviceArea || contact.serviceArea}`
              : ''}
          </p>
          {listing && listing.status !== 'approved' && (
            <p className={cn(ui.pillWarning, 'mt-2')}>{LISTING_STATUS_LABELS[listing.status]}</p>
          )}
        </div>
        {isOwner && (
          <Link className={ui.btnSecondary} to="/vendor">
            Edit in the vendor workspace
          </Link>
        )}
      </header>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          {vendor.description && (
            <section aria-labelledby="about-heading" className="mb-6">
              <h2 id="about-heading" className={ui.h2}>
                About
              </h2>
              <p className="whitespace-pre-line">{vendor.description}</p>
            </section>
          )}
          <section aria-labelledby="services-heading">
            <h2 id="services-heading" className={ui.h2}>
              Services and pricing
            </h2>
            {(vendor.services || []).length === 0 ? (
              <p className={ui.muted}>This vendor has not listed its services yet.</p>
            ) : (
              <ul className="m-0 list-none p-0">
                {vendor.services.map((s, i) => (
                  <li key={`${s.name}-${i}`} className={cn(ui.cardFlat, 'mb-3')}>
                    <div className={cn(ui.row, ui.spread)}>
                      <h3 className="m-0 text-title-s">{s.name}</h3>
                      <span className="font-semibold">{priceLabel(s)}</span>
                    </div>
                    {s.description && <p className="mt-1 mb-0 text-body-s">{s.description}</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {listing && <VendorReviews vendor={vendor} isOwner={isOwner} />}
        </div>
        <aside aria-labelledby="contact-heading">
          {listing?.status === 'approved' && !isOwner && (
            <JoinInterestList vendor={vendor} categories={listing.categories || []} />
          )}
          {rewardProgramLabel(listing?.rewardProgram) && (
            <p className={cn(ui.alertInfo, 'mb-6')}>
              {rewardProgramLabel(listing.rewardProgram)}.{' '}
              {['percent', 'flat'].includes(listing.rewardProgram.type)
                ? 'Members who recommend this vendor may earn a referral reward.'
                : ''}
              {listing.rewardProgram.terms && <span className="block">{listing.rewardProgram.terms}</span>}
            </p>
          )}
          <h2 id="contact-heading" className={ui.h2}>
            Contact
          </h2>
          <div className={ui.cardFlat}>
            {hasContact ? (
              <dl className="m-0 grid gap-2 text-body-s">
                {contact.email && (
                  <div>
                    <dt className="font-semibold">Email</dt>
                    <dd className="m-0">
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </dd>
                  </div>
                )}
                {contact.phone && (
                  <div>
                    <dt className="font-semibold">Phone</dt>
                    <dd className="m-0">
                      <a href={`tel:${contact.phone.replace(/[^+0-9]/g, '')}`}>{contact.phone}</a>
                    </dd>
                  </div>
                )}
                {contact.website && (
                  <div>
                    <dt className="font-semibold">Website</dt>
                    <dd className="m-0 break-all">
                      <a href={contact.website} target="_blank" rel="noopener noreferrer nofollow">
                        {contact.website}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className={cn(ui.muted, 'm-0 text-body-s')}>
                Contact details show to members once the vendor is listed here.
              </p>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}
